<script lang="ts">
  import { api } from '$shared/api';
  import { getUser } from '$lib/auth.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import ProfileTrophies from '$components/profile/ProfileTrophies.svelte';

  interface RecentDuel {
    id: number;
    duel_name: string;
    end_vote: string;
    track_type: string;
    genre_name: string;
    rank: number;
    my_votes: number;
    participant_count: number;
    total_votes: number;
  }
  interface StatsResponse {
    points: number;
    rank: number;
    total_players: number;
    duels_joined: number;
    duels_created: number;
    duels_won: number;
    win_rate: number | null;
    tracks_submitted: number;
    votes_cast: number;
    top_picks_given: number;
    tracks_listened: number;
    followers: number;
    following: number;
    votes_received: number;
    vote_streak: number;
    recent_duels: RecentDuel[];
  }
  interface Challenge {
    key: string;
    name: string;
    desc: string;
    progress: number;
    goal: number;
    completed: boolean;
  }
  interface GoodEar {
    total_duels: number;
    winner_picks: number;
    pct: number | null;
  }
  interface ListeningIdentity {
    contrarian_pct: number | null;
    early_adopter_pct: number | null;
    genre_breadth: number | null;
    taste_consistency_pct: number | null;
    listening_depth: number | null;
    sufficient_data: boolean;
  }

  const user = $derived(getUser());
  let data = $state<StatsResponse | null>(null);
  let challenges = $state<Challenge[]>([]);
  let goodEar = $state<GoodEar | null>(null);
  let identity = $state<ListeningIdentity | null>(null);
  let loading = $state(true);

  $effect(() => {
    const userId = user?.id;
    if (!userId) return;

    Promise.all([
      api.get<StatsResponse>('/users/current/stats'),
      api.get<{ challenges: Challenge[] }>(`/users/${userId}/challenges`).catch((e: unknown) => {
        console.warn('Challenges load failed:', e);
        return { challenges: [] as Challenge[] };
      }),
      api.get<GoodEar>(`/users/${userId}/good-ear`).catch((e: unknown) => {
        console.warn('Good ear load failed:', e);
        return null;
      }),
      api.get<ListeningIdentity>(`/users/${userId}/listening-identity`).catch((e: unknown) => {
        console.warn('Listening identity load failed:', e);
        return null;
      }),
    ])
      .then(([stats, ch, ge, li]) => {
        data = stats;
        challenges = ch.challenges;
        goodEar = ge;
        identity = li;
        loading = false;
      })
      .catch((e: unknown) => {
        console.error('Failed to load stats:', e);
        loading = false;
      });
  });

  const curatorTier = $derived.by(() => {
    if (!goodEar || goodEar.pct == null) return null;
    if (goodEar.pct >= 70 && goodEar.total_duels >= 10) return 'gold';
    if (goodEar.pct >= 55 && goodEar.total_duels >= 7) return 'silver';
    return null;
  });

  const identityMetrics = $derived(
    identity?.sufficient_data
      ? [
          { label: 'Contrarian Score', value: identity!.contrarian_pct!, desc: 'How often you vote against the crowd' },
          { label: 'Early Adopter', value: identity!.early_adopter_pct!, desc: 'How quickly you vote when duels open' },
          { label: 'Genre Explorer', value: identity!.genre_breadth!, desc: "How many genres you've explored" },
          { label: 'Taste Consistency', value: identity!.taste_consistency_pct!, desc: 'How often your top picks win' },
          { label: 'Listening Depth', value: identity!.listening_depth!, desc: 'Your listen-to-vote ratio' },
        ]
      : [],
  );

  const statCards = $derived(
    data
      ? [
          { label: 'Total Points', value: data.points.toLocaleString() },
          { label: 'Global Rank', value: `#${data.rank} of ${data.total_players}` },
          { label: 'Duels Joined', value: data.duels_joined },
          { label: 'Duels Won', value: data.duels_won },
          { label: 'Win Rate', value: data.win_rate != null ? `${data.win_rate}%` : '\u2014' },
          { label: 'Votes Received', value: data.votes_received },
          { label: 'Tracks Submitted', value: data.tracks_submitted },
          { label: 'Votes Cast', value: data.votes_cast },
          { label: 'Top Picks Given', value: data.top_picks_given },
          { label: 'Tracks Listened', value: data.tracks_listened },
          { label: 'Followers', value: data.followers },
          { label: 'Following', value: data.following },
          { label: 'Vote Streak', value: data.vote_streak > 0 ? `\u{1F525} ${data.vote_streak} days` : '\u2014' },
        ].concat(data.duels_created > 0 ? [{ label: 'Duels Created', value: data.duels_created }] : [])
      : [],
  );

  let sharing = $state(false);
  async function shareStatsCard() {
    sharing = true;
    try {
      const blob = await api.getBlob('/users/current/stats-card');

      if (navigator.share && navigator.canShare?.({ files: [new File([blob], 'stats.png', { type: 'image/png' })] })) {
        await navigator.share({
          title: 'My Beat Duel Stats',
          files: [new File([blob], 'beatduel-stats.png', { type: 'image/png' })],
        });
      } else {
        // Fallback: download the image
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'beatduel-stats.png';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') console.error('Share failed:', err);
    } finally {
      sharing = false;
    }
  }
</script>

<div>
  <h1>My Stats</h1>
  {#if loading}
    <LoadingSkeleton lines={6} />
  {:else if data}
    <div class="grid-stats">
      {#each statCards as card}
        <div class="stat-card">
          <span class="stat-value">{card.value}</span>
          <span class="stat-label">{card.label}</span>
        </div>
      {/each}
    </div>

    <div class="share-card-row">
      <button class="btn btn-sm btn-outline" onclick={shareStatsCard}>Share Your Stats</button>
    </div>

    <!-- Weekly Challenges -->
    {#if challenges.length > 0}
      <h2>Weekly Challenges</h2>
      <div class="challenges">
        {#each challenges as ch}
          <div class="challenge" class:challenge--done={ch.completed}>
            <div class="challenge-header">
              <span class="challenge-name">{ch.completed ? '\u2705' : '\u{1F3AF}'} {ch.name}</span>
              <span class="challenge-progress">{ch.progress}/{ch.goal}</span>
            </div>
            <p class="challenge-desc">{ch.desc}</p>
            <div class="challenge-bar">
              <div class="challenge-fill" style="width: {Math.min((ch.progress / ch.goal) * 100, 100)}%"></div>
            </div>
          </div>
        {/each}
      </div>
    {/if}

    <!-- Good Ear -->
    {#if goodEar && goodEar.total_duels >= 3}
      <h2>
        Good Ear{#if curatorTier}
          <span class="curator-tier curator-tier--{curatorTier}"
            >{curatorTier === 'gold' ? 'Gold' : 'Silver'} Curator</span
          >{/if}
      </h2>
      <div class="good-ear">
        <div class="good-ear-stat">
          <span class="good-ear-pct">{goodEar.pct}%</span>
          <span class="good-ear-label">of your votes picked the winner</span>
        </div>
        <span class="good-ear-detail">{goodEar.winner_picks} of {goodEar.total_duels} duels</span>
      </div>
    {/if}

    <!-- Listening Identity -->
    {#if identityMetrics.length > 0}
      <h2>Listening Identity</h2>
      <div class="identity-metrics">
        {#each identityMetrics as metric}
          <div class="identity-metric">
            <div class="identity-header">
              <span class="identity-label">{metric.label}</span>
              <span class="identity-value">{metric.value}%</span>
            </div>
            <div class="identity-bar">
              <div class="identity-bar-fill" style="width: {metric.value}%"></div>
            </div>
            <span class="identity-desc">{metric.desc}</span>
          </div>
        {/each}
      </div>
    {/if}

    <ProfileTrophies own />

    {#if data.recent_duels.length > 0}
      <h2>Duel Record</h2>
      <div class="table-scroll">
        <table class="data-table">
          <thead><tr><th>Duel</th><th>Genre</th><th>Type</th><th>Ended</th><th>Rank</th><th>Votes</th></tr></thead>
          <tbody>
            {#each data.recent_duels as duel}
              <tr>
                <td><a href="#/duels/{duel.id}/result">{duel.duel_name}</a></td>
                <td class="muted">{duel.genre_name}</td>
                <td class="muted type-badge">{duel.track_type}</td>
                <td class="muted">{new Date(duel.end_vote).toLocaleDateString()}</td>
                <td
                  class:rank-gold={duel.rank === 1}
                  class:rank-silver={duel.rank === 2}
                  class:rank-bronze={duel.rank === 3}
                  >#{duel.rank}<span class="of-count"> / {duel.participant_count}</span></td
                >
                <td class="muted">{duel.my_votes ?? 0}<span class="of-count"> / {duel.total_votes ?? 0}</span></td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  {/if}
</div>

<style>
  h1 {
    margin-bottom: 1.5rem;
  }
  h2 {
    margin-top: 2rem;
    margin-bottom: 0.75rem;
    font-size: 1.125rem;
  }
  .share-card-row {
    display: flex;
    justify-content: center;
    margin: 1rem 0;
  }
  .btn-outline {
    background: transparent;
    color: var(--cyan);
    border: 1px solid var(--cyan);
    border-radius: var(--radius);
    padding: 0.5rem 1.25rem;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 600;
    transition: background 0.15s;
  }
  .btn-outline:hover {
    background: rgba(0, 229, 255, 0.1);
  }
  .stat-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 1rem;
    text-align: center;
  }
  .stat-value {
    display: block;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--cyan);
  }
  .stat-label {
    font-size: 0.75rem;
    color: var(--text-secondary);
  }
  .data-table {
    width: 100%;
    font-size: 0.875rem;
    border-collapse: collapse;
  }
  .data-table th {
    text-align: left;
    padding-bottom: 0.5rem;
    color: var(--text-muted);
    font-weight: 500;
    border-bottom: 1px solid var(--border);
  }
  .data-table td {
    padding: 0.5rem 0;
    border-bottom: 1px solid rgba(42, 42, 42, 0.5);
  }
  .data-table a {
    color: var(--cyan);
  }
  .muted {
    color: var(--text-secondary);
  }
  .table-scroll {
    overflow-x: auto;
  }
  .rank-gold {
    color: var(--gold, #ffd700);
    font-weight: 700;
  }
  .rank-silver {
    color: #c0c0c0;
    font-weight: 600;
  }
  .rank-bronze {
    color: #cd7f32;
    font-weight: 600;
  }
  .of-count {
    font-size: 0.75rem;
    color: var(--text-muted);
  }
  .type-badge {
    text-transform: capitalize;
    font-size: 0.75rem;
  }

  /* Challenges */
  .challenges {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .challenge {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 0.75rem 1rem;
  }
  .challenge--done {
    border-color: #10b981;
  }
  .challenge-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .challenge-name {
    font-weight: 600;
    font-size: 0.875rem;
  }
  .challenge-progress {
    font-size: 0.75rem;
    color: var(--cyan);
    font-weight: 600;
  }
  .challenge-desc {
    font-size: 0.75rem;
    color: var(--text-muted);
    margin: 0.25rem 0 0.5rem;
  }
  .challenge-bar {
    height: 4px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
    overflow: hidden;
  }
  .challenge-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--pink), var(--purple));
    border-radius: 2px;
    transition: width 0.3s;
  }
  .challenge--done .challenge-fill {
    background: #10b981;
  }

  /* Good Ear */
  .good-ear {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .good-ear-stat {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
  }
  .good-ear-pct {
    font-size: 2rem;
    font-weight: 800;
    color: var(--cyan);
  }
  .good-ear-label {
    font-size: 0.875rem;
    color: var(--text-secondary);
  }
  .good-ear-detail {
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  /* Curator Tier */
  .curator-tier {
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
    vertical-align: middle;
  }
  .curator-tier--gold {
    background: rgba(255, 215, 0, 0.15);
    color: var(--gold, #ffd700);
  }
  .curator-tier--silver {
    background: rgba(192, 192, 192, 0.15);
    color: #c0c0c0;
  }

  /* Listening Identity */
  .identity-metrics {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .identity-metric {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 0.75rem 1rem;
  }
  .identity-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.375rem;
  }
  .identity-label {
    font-size: 0.875rem;
    font-weight: 600;
  }
  .identity-value {
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--cyan);
  }
  .identity-bar {
    height: 6px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 3px;
    overflow: hidden;
  }
  .identity-bar-fill {
    height: 100%;
    border-radius: 3px;
    background: linear-gradient(90deg, var(--cyan), var(--purple));
    transition: width 0.4s ease;
  }
  .identity-desc {
    font-size: 0.7rem;
    color: var(--text-muted);
    margin-top: 0.25rem;
    display: block;
  }
</style>
